/**
 * Offline Queue Utilities
 *
 * Manages offline data entry queueing using IndexedDB.
 * When the user is offline, data entries are stored locally
 * and synced when connectivity is restored.
 *
 * Per specs/01_purpose_scope_goals.md:
 * "Offline capability for core data entry (sync when online)"
 *
 * Per specs/07_daily_timeline_editing.md:
 * "Offline Editing: Allow edits offline, queue for sync"
 *
 * Uses pure functions for business logic, side effects isolated.
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Types of offline operations that can be queued
 */
export type OfflineOperationType =
  | "create_pain_entry"
  | "update_pain_entry"
  | "delete_pain_entry"
  | "create_event"
  | "update_event"
  | "delete_event";

/**
 * Sync status for queued items
 */
export type SyncStatus = "pending" | "syncing" | "synced" | "failed";

/**
 * A queued offline operation
 */
export interface QueuedOperation<T = unknown> {
  /** Unique ID for this queued operation */
  id: string;
  /** Type of operation */
  type: OfflineOperationType;
  /** The data to be synced */
  payload: T;
  /** Current sync status */
  status: SyncStatus;
  /** Timestamp when queued (ISO string) */
  queuedAt: string;
  /** Timestamp of last sync attempt (ISO string) */
  lastAttemptAt?: string;
  /** Number of sync attempts */
  attemptCount: number;
  /** Error message if failed */
  errorMessage?: string;
}

/**
 * Queue statistics
 */
export interface QueueStats {
  totalCount: number;
  pendingCount: number;
  syncingCount: number;
  failedCount: number;
  oldestQueuedAt: string | null;
}

// ============================================================================
// Constants
// ============================================================================

/** IndexedDB database name */
export const OFFLINE_DB_NAME = "floline-offline";

/** IndexedDB version */
export const OFFLINE_DB_VERSION = 1;

/** Store name for queued operations */
export const QUEUE_STORE_NAME = "operation-queue";

/** Maximum retry attempts before marking as failed */
export const MAX_RETRY_ATTEMPTS = 3;

/** Valid operation types */
export const VALID_OPERATION_TYPES: readonly OfflineOperationType[] = [
  "create_pain_entry",
  "update_pain_entry",
  "delete_pain_entry",
  "create_event",
  "update_event",
  "delete_event",
] as const;

// ============================================================================
// Pure Functions - ID Generation
// ============================================================================

/**
 * Generate a unique ID for a queued operation
 *
 * @returns Unique string ID
 */
export function generateQueueId(): string {
  return `q_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================================================
// Pure Functions - Queue Item Creation
// ============================================================================

/**
 * Create a new queued operation
 *
 * @param type - Operation type
 * @param payload - The data for the operation
 * @returns New queued operation with pending status
 */
export function createQueuedOperation<T>(
  type: OfflineOperationType,
  payload: T
): QueuedOperation<T> {
  return {
    id: generateQueueId(),
    type,
    payload,
    status: "pending",
    queuedAt: new Date().toISOString(),
    attemptCount: 0,
  };
}

// ============================================================================
// Pure Functions - Status Updates
// ============================================================================

/**
 * Mark a queued operation as syncing
 *
 * @param operation - The operation to update
 * @returns Updated operation with syncing status
 */
export function markSyncing<T>(operation: QueuedOperation<T>): QueuedOperation<T> {
  return {
    ...operation,
    status: "syncing",
    lastAttemptAt: new Date().toISOString(),
    attemptCount: operation.attemptCount + 1,
  };
}

/**
 * Mark a queued operation as synced (successful)
 *
 * @param operation - The operation to update
 * @returns Updated operation with synced status
 */
export function markSynced<T>(operation: QueuedOperation<T>): QueuedOperation<T> {
  return {
    ...operation,
    status: "synced",
    errorMessage: undefined,
  };
}

/**
 * Mark a queued operation as failed
 *
 * @param operation - The operation to update
 * @param errorMessage - Error description
 * @returns Updated operation with failed status if max retries exceeded, or pending for retry
 */
export function markFailed<T>(
  operation: QueuedOperation<T>,
  errorMessage: string
): QueuedOperation<T> {
  const shouldPermanentlyFail = operation.attemptCount >= MAX_RETRY_ATTEMPTS;
  return {
    ...operation,
    status: shouldPermanentlyFail ? "failed" : "pending",
    errorMessage,
  };
}

// ============================================================================
// Pure Functions - Queue Analysis
// ============================================================================

/**
 * Check if an operation type is valid
 *
 * @param type - String to check
 * @returns True if valid operation type
 */
export function isValidOperationType(type: string): type is OfflineOperationType {
  return VALID_OPERATION_TYPES.includes(type as OfflineOperationType);
}

/**
 * Filter operations by status
 *
 * @param operations - Array of operations
 * @param status - Status to filter by
 * @returns Filtered operations
 */
export function filterByStatus<T>(
  operations: QueuedOperation<T>[],
  status: SyncStatus
): QueuedOperation<T>[] {
  return operations.filter((op) => op.status === status);
}

/**
 * Get operations ready for sync (pending status)
 *
 * @param operations - Array of operations
 * @returns Pending operations sorted by queue time (oldest first)
 */
export function getPendingOperations<T>(
  operations: QueuedOperation<T>[]
): QueuedOperation<T>[] {
  return filterByStatus(operations, "pending").sort(
    (a, b) => new Date(a.queuedAt).getTime() - new Date(b.queuedAt).getTime()
  );
}

/**
 * Calculate queue statistics
 *
 * @param operations - Array of operations
 * @returns Queue statistics
 */
export function calculateQueueStats<T>(operations: QueuedOperation<T>[]): QueueStats {
  const pending = filterByStatus(operations, "pending");
  const syncing = filterByStatus(operations, "syncing");
  const failed = filterByStatus(operations, "failed");

  // Find oldest queued item
  let oldestQueuedAt: string | null = null;
  for (const op of operations) {
    if (op.status !== "synced") {
      if (!oldestQueuedAt || op.queuedAt < oldestQueuedAt) {
        oldestQueuedAt = op.queuedAt;
      }
    }
  }

  return {
    totalCount: operations.length,
    pendingCount: pending.length,
    syncingCount: syncing.length,
    failedCount: failed.length,
    oldestQueuedAt,
  };
}

/**
 * Check if queue has any unsynced items
 *
 * @param operations - Array of operations
 * @returns True if there are pending or failed items
 */
export function hasUnsyncedItems<T>(operations: QueuedOperation<T>[]): boolean {
  return operations.some((op) => op.status === "pending" || op.status === "failed");
}

/**
 * Check if queue has any failed items
 *
 * @param operations - Array of operations
 * @returns True if there are failed items
 */
export function hasFailedItems<T>(operations: QueuedOperation<T>[]): boolean {
  return operations.some((op) => op.status === "failed");
}

// ============================================================================
// Pure Functions - Operation Grouping
// ============================================================================

/**
 * Group operations by type
 *
 * @param operations - Array of operations
 * @returns Operations grouped by type
 */
export function groupByType<T>(
  operations: QueuedOperation<T>[]
): Map<OfflineOperationType, QueuedOperation<T>[]> {
  const groups = new Map<OfflineOperationType, QueuedOperation<T>[]>();

  for (const op of operations) {
    const existing = groups.get(op.type) ?? [];
    groups.set(op.type, [...existing, op]);
  }

  return groups;
}

/**
 * Count operations by type
 *
 * @param operations - Array of operations
 * @returns Count per operation type
 */
export function countByType<T>(
  operations: QueuedOperation<T>[]
): Record<OfflineOperationType, number> {
  const counts: Record<OfflineOperationType, number> = {
    create_pain_entry: 0,
    update_pain_entry: 0,
    delete_pain_entry: 0,
    create_event: 0,
    update_event: 0,
    delete_event: 0,
  };

  for (const op of operations) {
    counts[op.type]++;
  }

  return counts;
}

// ============================================================================
// Pure Functions - Display Helpers
// ============================================================================

/**
 * Get human-readable label for operation type
 *
 * @param type - Operation type
 * @returns Display label
 */
export function getOperationTypeLabel(type: OfflineOperationType): string {
  const labels: Record<OfflineOperationType, string> = {
    create_pain_entry: "Add pain entry",
    update_pain_entry: "Update pain entry",
    delete_pain_entry: "Delete pain entry",
    create_event: "Add event",
    update_event: "Update event",
    delete_event: "Delete event",
  };
  return labels[type];
}

/**
 * Get human-readable label for sync status
 *
 * @param status - Sync status
 * @returns Display label
 */
export function getSyncStatusLabel(status: SyncStatus): string {
  const labels: Record<SyncStatus, string> = {
    pending: "Waiting to sync",
    syncing: "Syncing...",
    synced: "Synced",
    failed: "Failed",
  };
  return labels[status];
}

/**
 * Format queue summary for display
 *
 * @param stats - Queue statistics
 * @returns Human-readable summary string
 */
export function formatQueueSummary(stats: QueueStats): string {
  if (stats.totalCount === 0) {
    return "All changes synced";
  }

  const parts: string[] = [];

  if (stats.pendingCount > 0) {
    parts.push(`${stats.pendingCount} pending`);
  }

  if (stats.syncingCount > 0) {
    parts.push(`${stats.syncingCount} syncing`);
  }

  if (stats.failedCount > 0) {
    parts.push(`${stats.failedCount} failed`);
  }

  return parts.join(", ");
}

// ============================================================================
// Pure Functions - Network Status Helpers
// ============================================================================

/**
 * Check if browser is online
 *
 * @returns True if navigator.onLine is true
 */
export function isOnline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine;
}

/**
 * Check if we should attempt sync
 *
 * @param isOnline - Network status
 * @param hasPending - Whether there are pending items
 * @returns True if sync should be attempted
 */
export function shouldAttemptSync(isOnline: boolean, hasPending: boolean): boolean {
  return isOnline && hasPending;
}
